import { apiClient } from '@/lib/api-client';
import type {
  Fluxograma,
  FluxogramaEdge,
  FluxogramaHistorico,
  FluxogramaNode,
  FluxogramaPermissao,
  FluxogramaTemplate,
  SnapshotEdge,
  SnapshotNode,
} from '@/types/fluxograma';

interface ApiResponse<T> {
  success: boolean;
  data: T;
  error?: string;
}

/** Resposta do salvamento; `id_map` traduz o id local (client_id) de formas/setas novas para o id do banco. */
export interface SnapshotSalvo {
  nodes: FluxogramaNode[];
  edges: FluxogramaEdge[];
  updatedAt?: string;
  id_map?: { nodes: Record<string, string>; edges: Record<string, string> };
}

export const fluxogramasService = {
  getAll(empresaId?: string): Promise<ApiResponse<Fluxograma[]>> {
    const query = empresaId ? `?empresa_id=${encodeURIComponent(empresaId)}` : '';
    return apiClient.get<ApiResponse<Fluxograma[]>>(`/fluxogramas${query}`);
  },
  getById(id: string): Promise<ApiResponse<Fluxograma>> {
    return apiClient.get<ApiResponse<Fluxograma>>(`/fluxogramas/${id}`);
  },
  create(data: Partial<Fluxograma> & { template_key?: string | null }): Promise<ApiResponse<Fluxograma>> {
    return apiClient.post<ApiResponse<Fluxograma>>('/fluxogramas', data);
  },
  update(id: string, data: Partial<Fluxograma>): Promise<ApiResponse<Fluxograma>> {
    return apiClient.put<ApiResponse<Fluxograma>>(`/fluxogramas/${id}`, data);
  },
  duplicate(id: string, nome?: string): Promise<ApiResponse<Fluxograma>> {
    return apiClient.post<ApiResponse<Fluxograma>>(`/fluxogramas/${id}/duplicate`, { nome });
  },
  archive(id: string): Promise<ApiResponse<Fluxograma>> {
    return apiClient.patch<ApiResponse<Fluxograma>>(`/fluxogramas/${id}/archive`);
  },
  delete(id: string): Promise<ApiResponse<boolean>> {
    return apiClient.delete<ApiResponse<boolean>>(`/fluxogramas/${id}`);
  },
  generateShareLink(id: string): Promise<ApiResponse<{ id: string; share_token: string; visibilidade: string }>> {
    return apiClient.post<ApiResponse<{ id: string; share_token: string; visibilidade: string }>>(
      `/fluxogramas/${id}/share`,
    );
  },
  revokeShareLink(id: string): Promise<ApiResponse<boolean>> {
    return apiClient.delete<ApiResponse<boolean>>(`/fluxogramas/${id}/share`);
  },
  getHistorico(id: string): Promise<ApiResponse<FluxogramaHistorico[]>> {
    return apiClient.get<ApiResponse<FluxogramaHistorico[]>>(`/fluxogramas/${id}/historico`);
  },
  getPermissoes(id: string): Promise<ApiResponse<FluxogramaPermissao[]>> {
    return apiClient.get<ApiResponse<FluxogramaPermissao[]>>(`/fluxogramas/${id}/permissoes`);
  },
  updatePermissoes(
    id: string,
    permissoes: Array<{ usuario_id: string; role: 'owner' | 'editor' | 'viewer' }>,
  ): Promise<ApiResponse<FluxogramaPermissao[]>> {
    return apiClient.put<ApiResponse<FluxogramaPermissao[]>>(`/fluxogramas/${id}/permissoes`, { permissoes });
  },
};

export const fluxogramaNodesService = {
  listNodes(fluxogramaId: string): Promise<ApiResponse<FluxogramaNode[]>> {
    return apiClient.get<ApiResponse<FluxogramaNode[]>>(
      `/fluxograma/nodes?fluxograma_id=${encodeURIComponent(fluxogramaId)}`,
    );
  },
  createNode(data: Partial<FluxogramaNode> & { fluxograma_id: string }): Promise<ApiResponse<FluxogramaNode>> {
    return apiClient.post<ApiResponse<FluxogramaNode>>('/fluxograma/nodes', data);
  },
  updateNode(id: string, data: Partial<FluxogramaNode>): Promise<ApiResponse<FluxogramaNode>> {
    return apiClient.put<ApiResponse<FluxogramaNode>>(`/fluxograma/nodes/${id}`, data);
  },
  deleteNode(id: string): Promise<ApiResponse<boolean>> {
    return apiClient.delete<ApiResponse<boolean>>(`/fluxograma/nodes/${id}`);
  },

  listEdges(fluxogramaId: string): Promise<ApiResponse<FluxogramaEdge[]>> {
    return apiClient.get<ApiResponse<FluxogramaEdge[]>>(
      `/fluxograma/edges?fluxograma_id=${encodeURIComponent(fluxogramaId)}`,
    );
  },
  createEdge(data: Partial<FluxogramaEdge> & { fluxograma_id: string }): Promise<ApiResponse<FluxogramaEdge>> {
    return apiClient.post<ApiResponse<FluxogramaEdge>>('/fluxograma/edges', data);
  },
  updateEdge(id: string, data: Partial<FluxogramaEdge>): Promise<ApiResponse<FluxogramaEdge>> {
    return apiClient.put<ApiResponse<FluxogramaEdge>>(`/fluxograma/edges/${id}`, data);
  },
  deleteEdge(id: string): Promise<ApiResponse<boolean>> {
    return apiClient.delete<ApiResponse<boolean>>(`/fluxograma/edges/${id}`);
  },

  saveSnapshot(payload: {
    fluxograma_id: string;
    nodes: SnapshotNode[];
    edges: SnapshotEdge[];
    updatedAt?: string | null;
  }): Promise<ApiResponse<SnapshotSalvo>> {
    return apiClient.post<ApiResponse<SnapshotSalvo>>(
      '/fluxograma/snapshot',
      payload,
    );
  },

  listTemplates(): Promise<ApiResponse<FluxogramaTemplate[]>> {
    return apiClient.get<ApiResponse<FluxogramaTemplate[]>>('/fluxograma/templates');
  },
};
